import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { dateKey, formatDate } from "./helpers";

const colors = {
  task: { bg: "#edf4ff", color: "#2456a6" },
  shift: { bg: "#fff3e6", color: "#995500" },
  season: { bg: "#e8f5ee", color: "#27684a" },
  event: { bg: "#f0edfa", color: "#65509b" },
};
export function monthRange(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 41);
  return { start: dateKey(start), end: dateKey(end), first: start };
}
export default function Calendar({
  month,
  onMonth,
  entries,
  onEntry,
  onAddDate,
  loading,
}) {
  const { first } = monthRange(month);
  const today = dateKey(new Date());
  const byDate = new Map();
  entries.forEach((entry) => {
    if (!byDate.has(entry.date)) byDate.set(entry.date, []);
    byDate.get(entry.date).push(entry);
  });
  const days = Array.from({ length: 42 }, (_, i) => {
    const day = new Date(first);
    day.setDate(first.getDate() + i);
    return day;
  });
  const monthPrefix = dateKey(month).slice(0, 7);
  const agenda = [...byDate.entries()]
    .filter(([day]) => day.startsWith(monthPrefix))
    .sort(([a], [b]) => a.localeCompare(b));
  const entryButton = (entry, index) => (
    <Button
      key={`${entry.type}-${entry.id}-${index}`}
      onClick={() => onEntry(entry)}
      title={`${entry.title}${entry.projected ? " (future repeat)" : ""}`}
      sx={{
        textTransform: "none",
        whiteSpace: "normal",
        justifyContent: "flex-start",
        textAlign: "left",
        width: "100%",
        minWidth: 0,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        color: colors[entry.kind].color,
        bgcolor: colors[entry.kind].bg,
        "&:hover": {
          bgcolor: colors[entry.kind].bg,
          filter: "brightness(.96)",
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          variant="caption"
          sx={{ display: "block", fontWeight: 600, overflowWrap: "anywhere" }}
        >
          {entry.title}
          {entry.projected ? " ↻" : ""}
        </Typography>
        {(entry.person_name || entry.start_time) && (
          <Typography
            component="span"
            sx={{ display: "block", fontSize: 11, opacity: 0.85 }}
          >
            {entry.start_time
              ? `${entry.start_time.slice(0, 5)}–${entry.end_time?.slice(0, 5)} · `
              : ""}
            {entry.person_name}
          </Typography>
        )}
      </Box>
    </Button>
  );
  return (
    <Stack spacing={2} aria-busy={loading}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center">
          <IconButton
            aria-label="Previous month"
            onClick={() =>
              onMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
            }
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 170, textAlign: "center" }}>
            {month.toLocaleDateString(undefined, {
              month: "long",
              year: "numeric",
            })}
          </Typography>
          <IconButton
            aria-label="Next month"
            onClick={() =>
              onMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
            }
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onMonth(new Date())}
        >
          Today
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {[
          ["task", "Tasks"],
          ["season", "Seasonal reminders"],
          ["shift", "Weekend shifts"],
          ["event", "Events"],
        ].map(([kind, label]) => (
          <Chip
            key={kind}
            size="small"
            label={label}
            sx={{ bgcolor: colors[kind].bg, color: colors[kind].color }}
          />
        ))}
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          overflow: "hidden",
        }}
      >
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <Typography
            key={day}
            variant="caption"
            textAlign="center"
            fontWeight={600}
            py={1.5}
            sx={{ bgcolor: "#f7f9fc", borderBottom: "1px solid #e7ebf0" }}
          >
            {day}
          </Typography>
        ))}
        {days.map((day) => {
          const key = dateKey(day);
          return (
            <Box
              key={key}
              sx={{
                minHeight: 120,
                p: 0.75,
                borderRight: "1px solid #edf0f4",
                borderBottom: "1px solid #edf0f4",
                bgcolor:
                  day.getMonth() !== month.getMonth() ? "#fafbfc" : "#fff",
              }}
            >
              <Button
                size="small"
                aria-label={`Add event on ${key}`}
                onClick={() => onAddDate(key)}
                sx={{
                  minWidth: 28,
                  height: 28,
                  mb: 0.5,
                  borderRadius: "50%",
                  color: key === today ? "#fff" : "text.secondary",
                  bgcolor: key === today ? "primary.main" : "transparent",
                }}
              >
                {day.getDate()}
              </Button>
              <Stack spacing={0.5}>
                {(byDate.get(key) || []).map(entryButton)}
              </Stack>
            </Box>
          );
        })}
      </Paper>
      <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" } }}>
        {agenda.map(([day, items]) => (
          <Paper variant="outlined" sx={{ p: 2 }} key={day}>
            <Typography fontWeight={600} mb={1}>
              {formatDate(day)}
            </Typography>
            <Stack spacing={1}>{items.map(entryButton)}</Stack>
          </Paper>
        ))}
      </Stack>
      {!loading && !agenda.length && (
        <Typography color="text.secondary" textAlign="center" py={3}>
          Nothing scheduled this month. Add an event or give a task a due date.
        </Typography>
      )}
    </Stack>
  );
}
